const baseUrl = 'https://calclab.app';

export const organizationSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "CalcLab",
  "description": "100+ free science and math calculators for students and educators",
  "url": baseUrl,
  "logo": "https://storage.googleapis.com/gpt-engineer-file-uploads/kqzygHPSMlZmS6bn0fteO3guI7C3/uploads/1762393922380-SparkleG.png",
  "sameAs": [],
  "inLanguage": language,
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "url": `${baseUrl}/support`
  }
});

export const websiteSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "CalcLab",
  "url": baseUrl,
  "description": "Free online calculators for physics, chemistry, finance, and math with step-by-step solutions",
  "inLanguage": language,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${baseUrl}/search?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

export const softwareApplicationSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "CalcLab - Science Calculators",
  "applicationCategory": "EducationalApplication",
  "operatingSystem": "Web Browser",
  "inLanguage": language,
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "1250",
    "bestRating": "5",
    "worstRating": "1"
  },
  "description": "100+ free calculators for physics, chemistry, finance, and math. Step-by-step solutions, LaTeX export, and AI-powered SuperCalc for complex problems.",
  "featureList": [
    "100+ Free Calculators",
    "Physics Tools",
    "Chemistry Tools", 
    "Finance Calculators",
    "Math Calculators",
    "Step-by-Step Solutions",
    "LaTeX Export",
    "SuperCalc AI",
    "Save Favorites",
    "No Registration Required"
  ]
});

export const faqSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "inLanguage": language,
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Are CalcLab calculators really free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! All 100+ calculators on CalcLab are completely free to use forever. No subscriptions, no hidden fees. Only SuperCalc AI requires a membership to cover compute costs."
      }
    },
    {
      "@type": "Question",
      "name": "What is SuperCalc AI?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "SuperCalc AI is our AI-powered calculator that can solve complex equations, provide step-by-step explanations, and help with difficult concepts. Free users get 1 free query, and members get 100-200 tokens per month (based on plan) with additional tokens available for purchase."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need to sign up to use calculators?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No! You can use all calculators without signing up. Creating a free account allows you to save your favorite calculators for quick access later."
      }
    },
    {
      "@type": "Question",
      "name": "What calculators are available?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CalcLab offers 100+ calculators across physics (mechanics, electricity, waves), chemistry (molarity, pH, stoichiometry), finance (compound interest, loans, investments), and math (equations, statistics, geometry)."
      }
    },
    {
      "@type": "Question",
      "name": "Can I export results to LaTeX?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! All calculators support LaTeX export for free. Perfect for academic papers, reports, and homework submissions in Overleaf, Google Docs, or Microsoft Word."
      }
    }
  ]
});

export const breadcrumbSchema = (items: { name: string; url: string }[], language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "inLanguage": language,
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const calculatorToolSchema = (name: string, description: string, url: string, language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": name,
  "description": description,
  "url": url,
  "applicationCategory": "EducationalApplication",
  "operatingSystem": "Web Browser",
  "inLanguage": language,
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "creator": {
    "@type": "Organization",
    "name": "CalcLab"
  }
});
